import React, { useState, useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { MessageType } from "@/types";
import MessageContainer, { MessageContainerRef } from "./MessageContainer";
import InputBox from "./InputBox";
import ChatHeader from "./ChatHeader";
import TopBanner from "./TopBanner";
import ErrorSnackbar from "./ErrorSnackbar";
import { useFetchAnswer } from "../hooks/useFetchAnswer";
import { useInitChat, useResetChatSession } from "../hooks/useInitChat";
import { useChatHistory } from "../hooks/useChatHistory";
import { useErrorStore } from "../store/error.store";
import { formatTimestamp } from "../utils/utils";
import { getThemeConfig } from "../theme/theme.manager";

/**
 * チャットボットメインコンポーネントのプロパティ
 */
interface ChatbotMainProps {
  isMaximized: boolean;
  onToggleMaximize: () => void;
  onChatClose: () => void;
}

/**
 * チャットボットメインコンポーネント
 * メッセージの表示、送信、フィードバック処理
 */
const ChatbotMain: React.FC<ChatbotMainProps> = ({
  isMaximized,
  onToggleMaximize,
  onChatClose,
}) => {
  const themeConfig = getThemeConfig();
  const setError = useErrorStore((state) => state.setError);
  const messageContainerRef = useRef<MessageContainerRef>(null);

  const {
    answerData,
    isLoading: isThinking,
    isAnswering,
    sendChatbotQuestion,
    handleFeedback,
  } = useFetchAnswer();

  // チャットボットのセッションを初期化
  const { sessionKey } = useInitChat();

  // メッセージ
  const [allMessages, setAllMessages] = useState<MessageType[]>([]);

  // チャット履歴の取得
  const { hasMore, loadMore, isLoadingMore, resetHistory } = useChatHistory(
    sessionKey,
    setAllMessages,
  );

  // 思考中のドットを表示する
  const [dotCount, setDotCount] = useState(0);

  // 思考中のドットカウント
  useEffect(() => {
    if (isThinking) {
      const interval = setInterval(() => {
        setDotCount((prev) => (prev < 7 ? prev + 1 : 1));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDotCount(0);
    }
  }, [isThinking]);

  // 回答データの設定
  useEffect(() => {
    if (answerData) {
      setAllMessages((prev: MessageType[]) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage?.isRobot) {
          return prev.map((msg) =>
            msg.id === lastMessage.id
              ? {
                  ...msg,
                  text: answerData.data,
                  id: answerData.id!,
                  shouldDisplayFeedback: answerData.shouldDisplayFeedback,
                  feedback: answerData.feedback,
                  referLinks: answerData.referLinks,
                  isFlow: answerData.isFlow,
                  flowData: answerData.flowData,
                }
              : msg,
          );
        } else {
          const timestamp = formatTimestamp(new Date());
          return [
            ...prev,
            {
              id: answerData.id!,
              text: answerData.data,
              sent: false,
              timestamp,
              avatar: "AI",
              isRobot: true,
              shouldDisplayFeedback: answerData.shouldDisplayFeedback,
              feedback: answerData.feedback,
              referLinks: answerData.referLinks,
              isFlow: answerData.isFlow,
              flowData: answerData.flowData,
            },
          ];
        }
      });
    }
  }, [answerData]);

  const isThinkingText = `${"●".repeat(dotCount)}`;

  // メッセージ送信処理
  const handleSend = async (message: string) => {
    if (!message.trim()) {
      return;
    }

    if (message.length > 200) {
      setError("２００文字以内で入力してください。");
      return;
    }

    const timestamp = formatTimestamp(new Date());
    const messageId = uuidv4();

    const userMessage: MessageType = {
      id: messageId,
      text: message,
      sent: true,
      avatar: "User",
      isRobot: false,
      timestamp,
    };

    setAllMessages((prev: MessageType[]) => [...prev, userMessage]);

    messageContainerRef.current?.scrollToBottom("smooth");

    try {
      /**
       * メッセージの送信
       */
      await sendChatbotQuestion(messageId, message);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("メッセージ送信中にエラーが発生しました");
      }
    }
  };

  // Likeフィードバック処理
  const handleLike = async (id: string) => {
    const message = allMessages.find((msg) => msg.id === id);
    if (!message?.id) return;

    setAllMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, feedback: "like" } : msg)),
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messageContainerRef.current?.scrollToBottom("smooth");
      });
    });

    try {
      const results = await handleFeedback(message.id, true);
      if (!results || !results.success) {
        setAllMessages((prev) =>
          prev.map((msg) => (msg.id === id ? { ...msg, feedback: "--" } : msg)),
        );
        setError("フィードバックの送信に失敗しました");
      } else {
        for (const result of results.data || []) {
          const timestamp = formatTimestamp(result.timestamp);
          setAllMessages((prev) => [
            ...prev,
            {
              id: result.message_id,
              text: result.text,
              sent: !result.isRobot,
              timestamp: timestamp,
              avatar: result.avatar,
              isRobot: result.isRobot,
              shouldDisplayFeedback: result.should_display_feedback,
              feedback: "--",
              referLinks: [],
              isFlow: result.isFlow,
              flowData: result.isFlow
                ? {
                    flowType: result.flowType!,
                    flowStepId: result.flowStepId!,
                    flowEnded: false,
                    choices: result.choices || [],
                    linkButtons: result.linkButtons || [],
                  }
                : undefined,
            },
          ]);
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            messageContainerRef.current?.scrollToBottom("smooth");
          });
        });
      }
    } catch (error) {
      setAllMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, feedback: "--" } : msg)),
      );
      console.error("フィードバック送信に失敗しました:", error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("フィードバックの送信に失敗しました");
      }
    }
  };

  // Dislikeフィードバック処理
  const handleDislike = async (id: string) => {
    const message = allMessages.find((msg) => msg.id === id);
    if (!message?.id) return;

    setAllMessages((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, feedback: "dislike" } : msg,
      ),
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messageContainerRef.current?.scrollToBottom("smooth");
      });
    });

    try {
      const results = await handleFeedback(message.id, false);
      if (!results || !results.success) {
        setAllMessages((prev) =>
          prev.map((msg) => (msg.id === id ? { ...msg, feedback: "--" } : msg)),
        );
        setError("フィードバックの送信に失敗しました");
      } else {
        for (const result of results.data || []) {
          const timestamp = formatTimestamp(result.timestamp);
          setAllMessages((prev) => [
            ...prev,
            {
              id: result.message_id,
              text: result.text,
              sent: !result.isRobot,
              timestamp: timestamp,
              avatar: result.avatar,
              isRobot: result.isRobot,
              shouldDisplayFeedback: result.should_display_feedback,
              feedback: "--",
              referLinks: [],
              isFlow: result.isFlow,
              flowData: result.isFlow
                ? {
                    flowType: result.flowType!,
                    flowStepId: result.flowStepId!,
                    flowEnded: false,
                    choices: result.choices || [],
                    linkButtons: result.linkButtons || [],
                  }
                : undefined,
            },
          ]);
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            messageContainerRef.current?.scrollToBottom("smooth");
          });
        });
      }
    } catch (error) {
      setAllMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, feedback: "--" } : msg)),
      );
      console.error("フィードバック送信に失敗しました:", error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("フィードバックの送信に失敗しました");
      }
    }
  };

  // 選択肢押下処理
  const handleFlowChoice = async (choice: { id: string; label: string }) => {
    // 一番後ろのフローメッセージからカレントのflowDataを取得する
    let currentFlow: MessageType["flowData"] | undefined;
    for (let i = allMessages.length - 1; i >= 0; i--) {
      if (allMessages[i].isFlow && allMessages[i].flowData) {
        currentFlow = allMessages[i].flowData;
        break;
      }
    }
    if (!currentFlow) return;

    // 押した選択肢をユーザーメッセージとする
    const timestamp = formatTimestamp(new Date());
    const messageId = uuidv4();
    setAllMessages((prev) => [
      ...prev,
      {
        id: messageId,
        text: choice.label,
        sent: true,
        avatar: "User",
        isRobot: false,
        timestamp,
      },
    ]);

    messageContainerRef.current?.scrollToBottom("smooth");

    try {
      await sendChatbotQuestion(messageId, choice.label, {
        flowType: currentFlow.flowType,
        flowStepId: currentFlow.flowStepId,
        flowButtonId: choice.id,
      });
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("フロー選択肢押下にエラーが発生しました");
      }
    }
  };

  // メッセージを整理する
  const boundMessages = useMemo(() => {
    let lastFlowIndex = -1;
    for (let i = allMessages.length - 1; i >= 0; i--) {
      if (allMessages[i].isFlow) {
        lastFlowIndex = i;
        break;
      }
    }

    return allMessages.map((msg: MessageType, i) => ({
      ...msg,
      onLike: () => handleLike(msg.id),
      onDislike: () => handleDislike(msg.id),
      isAnswering:
        isAnswering && msg.id === allMessages[allMessages.length - 1]?.id,
      botName: themeConfig.chatbotName,
      isChoiceDisabled: msg.isFlow
        ? i !== lastFlowIndex || isAnswering
        : undefined,
      onFlowChoice: msg.isFlow ? handleFlowChoice : undefined,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMessages, isAnswering, themeConfig.chatbotName]);

  // 会話をリセットする
  const { resetSession, resetSessionKey } = useResetChatSession();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null);

  // Dialogの解除関数をクリーンアップ時にnullにする
  useEffect(() => {
    return () => {
      confirmResolveRef.current = null;
    };
  }, []);

  // セッションをリセットする処理
  const onSessionReset = async () => {
    // 確認ダイアログを表示
    const confirmed = await showConfirmDialog();
    if (confirmed) {
      const result = await resetSession();
      if (result.success) {
        await resetHistory();
        resetSessionKey(result.key);
      }
    }
  };

  // 確認ダイアログを表示し、ユーザーの選択をPromiseで返す
  const showConfirmDialog = (): Promise<boolean> => {
    return new Promise((resolve) => {
      setShowConfirm(true);
      confirmResolveRef.current = resolve;
    });
  };

  // キャンセルボタンクリック時の処理
  const handleCancel = () => {
    setShowConfirm(false);
    if (confirmResolveRef.current) {
      confirmResolveRef.current(false);
      confirmResolveRef.current = null;
    }
  };

  // 確認（リセット）ボタンクリック時の処理
  const handleConfirmReset = async () => {
    if (isResetting) return;
    setIsResetting(true);
    setShowConfirm(false);
    if (confirmResolveRef.current) {
      confirmResolveRef.current(true);
      confirmResolveRef.current = null;
    }
    setIsResetting(false);
  };

  return (
    <div
      className={`pathbee-chatbot-container flex flex-col h-full ${
        isMaximized ? "rounded-none shadow-none" : "rounded-3xl shadow-lg"
      } transition-all duration-200 ease-out will-change-transform`}
      style={{ backgroundColor: "var(--main-bg)" }}
    >
      {showConfirm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-400/60">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
            className="bg-white rounded-3xl shadow-xl w-94 overflow-hidden"
          >
            <div className="flex flex-col items-center py-8 px-6">
              <p className="text-gray-800 text-sm text-center leading-relaxed mb-6">
                会話をリセットして、新しい会話を開始しますか？
                <br />
                リセットした会話は戻せません。
              </p>
              <div className="flex gap-4 w-full">
                <button
                  onClick={handleCancel}
                  className={`flex-1 px-4 py-2 border border-gray-400 rounded-md text-gray-700 
                    hover:bg-gray-50 text-sm font-medium cursor-pointer`}
                >
                  キャンセル
                </button>
                <button
                  onClick={handleConfirmReset}
                  disabled={isResetting}
                  className={`flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm 
                    font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
                >
                  リセット
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ChatHeader
        isMaximized={isMaximized}
        onToggleMaximize={onToggleMaximize}
        onChatClose={onChatClose}
        onSessionReset={onSessionReset}
        title={themeConfig.chatbotTitle}
      />

      <div
        className={`flex flex-col mt-1 flex-1 overflow-hidden transition-all duration-200 ease-out ${
          isMaximized ? "px-4" : ""
        }`}
        style={{
          maxWidth: isMaximized ? "1100px" : "none",
          margin: isMaximized ? "0 auto" : "0",
          width: "100%",
        }}
      >
        <TopBanner />

        <MessageContainer
          ref={messageContainerRef}
          messages={boundMessages}
          isThinking={isThinking}
          isThinkingText={isThinkingText}
          isAnswering={isAnswering}
          onLoadMore={loadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />

        <div className="relative flex justify-center w-full h-0">
          <ErrorSnackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
              position: "absolute",
              bottom: "20px",
              width: "100%",
              zIndex: 1000,
            }}
          />
        </div>

        <div
          className="min-h-[30px] flex rounded-b-3xl"
          style={{ backgroundColor: "var(--footer-bg)" }}
        >
          <InputBox onSendMessage={handleSend} disabled={isAnswering} />
        </div>
      </div>
    </div>
  );
};

export default ChatbotMain;
